import React from "react";
import { inr, grams } from "@/lib/api";
import { imageSrc } from "@/components/ImageUpload";

const toNum = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Printable invoice (A4 + thermal-friendly).
 * Pass bill and settings (store branding) as props.
 */
export default function PrintableInvoice({ bill, settings, mode = "a4" }) {
  if (!bill) return null;
  const isThermal = mode === "thermal";
  return (
    <div
      className={`print-container ${isThermal ? "thermal" : "a4"} bg-white text-black p-6 mx-auto`}
      style={{ maxWidth: isThermal ? 320 : 800 }}
    >
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .print-container, .print-container * { visibility: visible !important; }
          .print-container { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
        .print-container { font-family: 'Helvetica', 'Arial', sans-serif; color: #000; }
        .print-container.thermal { font-size: 11px; }
        .print-container table { width: 100%; border-collapse: collapse; }
        .print-container th, .print-container td { padding: 4px 6px; font-size: ${isThermal ? "10px" : "12px"}; }
        .print-container th { border-bottom: 1px solid #000; text-align: left; }
        .print-container td.num { text-align: right; font-variant-numeric: tabular-nums; }
      `}</style>

      <div className="text-center mb-3">
        {settings?.logo_url && (
          <img
            src={imageSrc(settings.logo_url)}
            alt=""
            style={{ maxHeight: 48, margin: "0 auto 6px" }}
            crossOrigin="anonymous"
          />
        )}
        <div
          style={{
            fontSize: isThermal ? 14 : 22,
            fontWeight: 700,
            letterSpacing: 1,
          }}
        >
          {settings?.store_name || "JEWELLERY STORE"}
        </div>
        <div style={{ fontSize: isThermal ? 9 : 11 }}>{settings?.address}</div>
        <div style={{ fontSize: isThermal ? 9 : 11 }}>
          {settings?.phone ? `Ph: ${settings.phone}` : ""}
          {settings?.gst_number ? `   GSTIN: ${settings.gst_number}` : ""}
        </div>
      </div>

      <div
        style={{
          borderTop: "1px dashed #000",
          borderBottom: "1px dashed #000",
          padding: "6px 0",
          margin: "6px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: isThermal ? 10 : 12,
          }}
        >
          <span>
            <b>Invoice:</b> {bill.invoice_no}
          </span>
          <span>{new Date(bill.created_at).toLocaleString("en-IN")}</span>
        </div>
        <div style={{ fontSize: isThermal ? 10 : 12 }}>
          <b>Customer:</b> {bill.customer_name || "Walk-in"}
          {bill.customer_mobile ? ` · ${bill.customer_mobile}` : ""}
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th className="num">Wt(g)</th>
            <th className="num">Rate</th>
            <th className="num">Qty</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {bill.items?.map((it, i) => (
            <tr key={i} style={{ borderBottom: "1px dotted #999" }}>
              <td>
                {it.name}
                <br />
                <span style={{ fontSize: 9, color: "#444" }}>{it.purity}</span>
              </td>
              <td className="num">{toNum(it.net_weight).toFixed(3)}</td>
              <td className="num">{toNum(it.rate_per_gram).toFixed(0)}</td>
              <td className="num">{toNum(it.quantity)}</td>
              <td className="num">{toNum(it.amount).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 8, fontSize: isThermal ? 10 : 12 }}>
        <Row l="Subtotal" v={bill.sub_total} />
        {bill.discount > 0 && <Row l="Discount" v={-bill.discount} />}
        <Row l={`GST (${bill.gst_percent}%)`} v={bill.gst_amount} />
        {bill.old_gold_value > 0 && (
          <Row l="Less: Old gold" v={-bill.old_gold_value} />
        )}
        {bill.round_off !== 0 && <Row l="Round off" v={bill.round_off} />}
        <div
          style={{
            borderTop: "1px solid #000",
            marginTop: 4,
            paddingTop: 4,
            display: "flex",
            justifyContent: "space-between",
            fontSize: isThermal ? 12 : 16,
            fontWeight: 700,
          }}
        >
          <span>TOTAL</span>
          <span>{inr(bill.grand_total)}</span>
        </div>
        <Row l="Paid" v={bill.paid} />
        {bill.balance > 0 && <Row l="Balance" v={bill.balance} />}
      </div>

      <div
        style={{
          marginTop: 10,
          textAlign: "center",
          fontSize: isThermal ? 9 : 11,
        }}
      >
        {settings?.invoice_footer || "Thank you for shopping with us!"}
      </div>
    </div>
  );
}

const Row = ({ l, v }) => (
  <div style={{ display: "flex", justifyContent: "space-between" }}>
    <span>{l}</span>
    <span style={{ fontVariantNumeric: "tabular-nums" }}>{inr(v)}</span>
  </div>
);
